import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { InnerPagesApiService } from './inner-pages-api.service';
import { getStorageItem, STORAGE_KEYS } from 'src/@shared/storage/app-storage';

/**
 * Extends {@link InnerPagesApiService} for all list/overview HTTP APIs and adds
 * filters, subjects, and `canAccess*` permission helpers.
 */
@Injectable({
  providedIn: 'root',
})
export class InnerPagesService extends InnerPagesApiService {
  rolePermissions: any;

  filterObj$ = new BehaviorSubject<any>({
    agencyIds: '',
    accountIds: '',
    siteIds: '',
    type: 1,
  });
  dashboardObj$ = new BehaviorSubject<any>({
    agencyIds: '',
    accountIds: '',
    siteIds: '',
  });

  constructor(_cS: CommonService) {
    super(_cS);
  }

  getRolePermissionsDetailsFromLocalStorage() {
    this.rolePermissions = JSON.parse(
      getStorageItem(STORAGE_KEYS.rolePermissions) || '[]'
    );
  }

  private static readonly BrooklynModuleType = 'Brooklyn';
  private static readonly ProjectionForecastModule = 'Brooklyn.Projections.Forecast';
  private static readonly ProjectionGoalModule = 'Brooklyn.Projections.Goal';
  private static readonly ProjectionReportModule = 'Brooklyn.Projections.Report';
  private static readonly ProjectionLegacyModule = 'Brooklyn.Projections';
  private static readonly UsersModule = 'Brooklyn.Users';
  private static readonly VendorsModule = 'Brooklyn.Vendors';
  private static readonly ExpensesModule = 'Brooklyn.Expenses';
  private static readonly InvestmentsModule = 'Brooklyn.Investments';
  private static readonly ReportsModule = 'Brooklyn.Reports';
  private static readonly MaintenanceModule = 'Maintenance';
  private static readonly TermsModule = 'Terms';

  /** Find a leaf by module name. Defaults to Brooklyn so Paybev/Supplier "Reports" do not collide. */
  private findPermissionLeaf(
    moduleName: string,
    moduleType: string = InnerPagesService.BrooklynModuleType
  ): { canView?: boolean; canAdd?: boolean; canEdit?: boolean; canDelete?: boolean } | null {
    this.getRolePermissionsDetailsFromLocalStorage();
    for (const group of this.rolePermissions ?? []) {
      if (moduleType && group?.moduleType !== moduleType) {
        continue;
      }
      const leaf = group?.permissions?.find((p: { module?: string }) => p?.module === moduleName);
      if (leaf) {
        return leaf;
      }
    }
    return null;
  }
  //check if current user can access users
  canAccessUsers() {
    this.getRolePermissionsDetailsFromLocalStorage();
    const leaf = this.findPermissionLeaf(InnerPagesService.UsersModule);
    return {
      canViewUsers: leaf?.canView,
      canAddUser: leaf?.canAdd,
      canEditUser: leaf?.canEdit,
      canDeleteUser: leaf?.canDelete,
    };
  }
  //check if current role can access Roles
  canAccessRoles() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewRole, canAddRole, canEditRole, canDeleteRole;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Roles'
    );
    if (index != undefined && index != -1) {
      canViewRole = adminDetails.permissions[index].canView;
      canAddRole = adminDetails.permissions[index].canAdd;
      canEditRole = adminDetails.permissions[index].canEdit;
      canDeleteRole = adminDetails.permissions[index].canDelete;
    }
    return { canViewRole, canAddRole, canEditRole, canDeleteRole };
  }

  //check if current user can access Agency
  canAccessAgencys() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewAgency, canAddAgency, canEditAgency, canDeleteAgency;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Agency'
    );
    if (index != undefined && index != -1) {
      canViewAgency = adminDetails.permissions[index].canView;
      canAddAgency = adminDetails.permissions[index].canAdd;
      canEditAgency = adminDetails.permissions[index].canEdit;
      canDeleteAgency = adminDetails.permissions[index].canDelete;
    }
    return { canViewAgency, canAddAgency, canEditAgency, canDeleteAgency };
  }

  //check if current user can access Account
  canAccessAccounts() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewAccounts, canAddAccounts, canEditAccounts, canDeleteAccounts;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Accounts'
    );
    if (index != undefined && index != -1) {
      canViewAccounts = adminDetails.permissions[index].canView;
      canAddAccounts = adminDetails.permissions[index].canAdd;
      canEditAccounts = adminDetails.permissions[index].canEdit;
      canDeleteAccounts = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewAccounts,
      canAddAccounts,
      canEditAccounts,
      canDeleteAccounts,
    };
  }
  canAccessAccountTypes() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewAccounts, canAddAccounts, canEditAccounts, canDeleteAccounts;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Account Types'
    );
    if (index != undefined && index != -1) {
      canViewAccounts = adminDetails.permissions[index].canView;
      canAddAccounts = adminDetails.permissions[index].canAdd;
      canEditAccounts = adminDetails.permissions[index].canEdit;
      canDeleteAccounts = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewAccounts,
      canAddAccounts,
      canEditAccounts,
      canDeleteAccounts,
    };
  }
  //resources to list on paybev summary
  canAccessResources() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewResources, canAddResource, canEditResource, canDeleteResource;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Resources'
    );
    if (index != undefined && index != -1) {
      canViewResources = adminDetails.permissions[index].canView;
      canAddResource = adminDetails.permissions[index].canAdd;
      canEditResource = adminDetails.permissions[index].canEdit;
      canDeleteResource = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewResources,
      canAddResource,
      canEditResource,
      canDeleteResource,
    };
  }
  //check if current user can access Sites
  canAccessSites() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewSites, canAddSites, canEditSites, canDeleteSites;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Sites'
    );
    if (index != undefined && index != -1) {
      canViewSites = adminDetails.permissions[index].canView;
      canAddSites = adminDetails.permissions[index].canAdd;
      canEditSites = adminDetails.permissions[index].canEdit;
      canDeleteSites = adminDetails.permissions[index].canDelete;
    }
    return { canViewSites, canAddSites, canEditSites, canDeleteSites };
  }

  //check if current user can access Suppliers
  canAccessSuppliers() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewSuppliers, canAddSuppliers, canEditSuppliers, canDeleteSuppliers;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Suppliers'
    );
    if (index != undefined && index != -1) {
      canViewSuppliers = adminDetails.permissions[index].canView;
      canAddSuppliers = adminDetails.permissions[index].canAdd;
      canEditSuppliers = adminDetails.permissions[index].canEdit;
      canDeleteSuppliers = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewSuppliers,
      canAddSuppliers,
      canEditSuppliers,
      canDeleteSuppliers,
    };
  }

  //check if current user can access Reps
  canAccessReps() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewReps, canAddReps, canEditReps, canDeleteReps;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Reps'
    );
    if (index != undefined && index != -1) {
      canViewReps = adminDetails.permissions[index].canView;
      canAddReps = adminDetails.permissions[index].canAdd;
      canEditReps = adminDetails.permissions[index].canEdit;
      canDeleteReps = adminDetails.permissions[index].canDelete;
    }
    return { canViewReps, canAddReps, canEditReps, canDeleteReps };
  }

  //check if current user can access vendors
  canAccessVendors() {
    this.getRolePermissionsDetailsFromLocalStorage();
    const leaf = this.findPermissionLeaf(InnerPagesService.VendorsModule);
    return {
      canViewVendors: leaf?.canView,
      canAddVendors: leaf?.canAdd,
      canEditVendors: leaf?.canEdit,
      canDeleteVendors: leaf?.canDelete,
    };
  }

  //check if current user can access Maintainance
  canAccessMaintainance() {
    this.getRolePermissionsDetailsFromLocalStorage();
    const leaf = this.findPermissionLeaf(InnerPagesService.MaintenanceModule);
    return {
      canViewUsers: leaf?.canView,
      canAddUser: leaf?.canAdd,
      canEditUser: leaf?.canEdit,
      canDeleteUser: leaf?.canDelete,
    };
  }
  //check if current user can access Maintainance
  canAccessTransfers() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewTransfers, canAddBT, canEditBT, canDeleteBT;
    var adminDetails = this.rolePermissions[0];

    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Transfers'
    );
    if (index != undefined && index != -1) {
      canViewTransfers = adminDetails.permissions[index].canView;
      canAddBT = adminDetails.permissions[index].canAdd;
      canEditBT = adminDetails.permissions[index].canEdit;
      canDeleteBT = adminDetails.permissions[index].canDelete;
    }
    return { canViewTransfers, canAddBT, canEditBT, canDeleteBT };
  }
  //check if current user can access Maintainance
  // canAccessInternalTransfers() {
  //   this.getRolePermissionsDetailsFromLocalStorage();
  //   var canViewITs, canAddIT, canEditIT, canDeleteIT;
  //   var adminDetails = this.rolePermissions.find(
  //     (obj: any) => obj.header == 'Transfers'
  //   );
  //   var index = adminDetails?.permissions?.findIndex(
  //     (obj: any) => obj.module == 'Internal Transfers'
  //   );
  //   if (index != undefined && index != -1) {
  //     canViewITs = adminDetails.permissions[index].canView;
  //     canAddIT = adminDetails.permissions[index].canAdd;
  //     canEditIT = adminDetails.permissions[index].canEdit;
  //     canDeleteIT = adminDetails.permissions[index].canDelete;
  //   }
  //   return { canViewITs, canAddIT, canEditIT, canDeleteIT };
  // }

  //check if current user can access Announcement
  canAccessAnnouncement() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewAnnouncements,
      canAddAnnouncements,
      canEditAnnouncements,
      canDeleteAnnouncements;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Announcements'
    );
    if (index != undefined && index != -1) {
      canViewAnnouncements = adminDetails.permissions[index].canView;
      canAddAnnouncements = adminDetails.permissions[index].canAdd;
      canEditAnnouncements = adminDetails.permissions[index].canEdit;
      canDeleteAnnouncements = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewAnnouncements,
      canAddAnnouncements,
      canEditAnnouncements,
      canDeleteAnnouncements,
    };
  }

  canAccessRetailPrograms() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewRetailPrograms,
      canAddRetailPrograms,
      canEditRetailPrograms,
      canDeleteRetailPrograms;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );

    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'RetailProgram'
    );
    if (index != undefined && index != -1) {
      canViewRetailPrograms = adminDetails.permissions[index].canView;
      canAddRetailPrograms = adminDetails.permissions[index].canAdd;
      canEditRetailPrograms = adminDetails.permissions[index].canEdit;
      canDeleteRetailPrograms = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewRetailPrograms,
      canAddRetailPrograms,
      canEditRetailPrograms,
      canDeleteRetailPrograms,
    };
  }

  //check if current user can access Bills
  canAccessBills() {
    this.getRolePermissionsDetailsFromLocalStorage();
    const leaf = this.findPermissionLeaf(InnerPagesService.ExpensesModule);
    return {
      canViewExpenses: leaf?.canView,
      canAddExpense: leaf?.canAdd,
      canEditExpense: leaf?.canEdit,
      canDeleteExpense: leaf?.canDelete,
    };
  }
  //check if current user can access Estimates
  canAccessInvestments() {
    this.getRolePermissionsDetailsFromLocalStorage();
    const leaf = this.findPermissionLeaf(InnerPagesService.InvestmentsModule);
    return {
      canViewInvestment: leaf?.canView,
      canAddInvestment: leaf?.canAdd,
      canEditInvestment: leaf?.canEdit,
      canDeleteInvestment: leaf?.canDelete,
    };
  }
  canAccessBadges() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewBadge, canAddBadge, canEditBadge, canDeleteBadge;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Badges'
    );
    if (index != undefined && index != -1) {
      canViewBadge = adminDetails.permissions[index].canView;
      canAddBadge = adminDetails.permissions[index].canAdd;
      canEditBadge = adminDetails.permissions[index].canEdit;
      canDeleteBadge = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewBadge,
      canAddBadge,
      canEditBadge,
      canDeleteBadge,
    };
  }
  canAccessUnderReview() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewUnderReview,
      canAddUnderReview,
      canEditUnderReview,
      canDeleteUnderReview;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Under Review'
    );
    if (index != undefined && index != -1) {
      canViewUnderReview = adminDetails.permissions[index].canView;
      canAddUnderReview = adminDetails.permissions[index].canAdd;
      canEditUnderReview = adminDetails.permissions[index].canEdit;
      canDeleteUnderReview = adminDetails.permissions[index].canDelete;
    }
    return {
      canViewUnderReview,
      canAddUnderReview,
      canEditUnderReview,
      canDeleteUnderReview,
    };
  }
  //check if current user can access Terms (no Delete: a Terms version is never deleted)
  canAccessTerms() {
    const leaf = this.findPermissionLeaf(InnerPagesService.TermsModule);
    return {
      canViewTerms: !!leaf?.canView,
      canAddTerms: !!leaf?.canAdd,
      canEditTerms: !!leaf?.canEdit,
    };
  }
  canAccessGeneral() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewGeneral, canEditGeneral;
    var adminDetails = this.rolePermissions.find(
      (obj: any) => obj.header == 'Settings'
    );
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'General'
    );
    if (index != undefined && index != -1) {
      canViewGeneral = adminDetails.permissions[index].canView;
      canEditGeneral = adminDetails.permissions[index].canEdit;
    }
    return {
      canViewGeneral,
      canEditGeneral,
    };
  }
  canviewOverview() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewOverview;
    var adminDetails = this.rolePermissions[0];
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Overview'
    );
    if (index != undefined && index != -1) {
      canViewOverview = adminDetails?.permissions[index]?.canView;
    }
    return canViewOverview;
  }
  canViewDashboard() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewDashboard;
    if (this.rolePermissions) {
      var adminDetails = this.rolePermissions[0];
      var index = adminDetails?.permissions?.findIndex(
        (obj: any) => obj.module == 'Dashboard'
      );
      if (index != undefined && index != -1) {
        canViewDashboard = adminDetails.permissions[index].canView;
      }
    }
    return canViewDashboard;
  }
  canViewReports() {
    return !!this.findPermissionLeaf(InnerPagesService.ReportsModule)?.canView;
  }

  canAccessProjections() {
    const forecast = this.findPermissionLeaf(InnerPagesService.ProjectionForecastModule);
    const goal = this.findPermissionLeaf(InnerPagesService.ProjectionGoalModule);
    const report = this.findPermissionLeaf(InnerPagesService.ProjectionReportModule);
    const legacy = this.findPermissionLeaf(InnerPagesService.ProjectionLegacyModule);

    const canViewForecast = !!(forecast?.canView ?? legacy?.canView);
    const canEditForecast = !!(forecast?.canEdit ?? legacy?.canEdit);
    const canViewGoal = !!(goal?.canView ?? legacy?.canView);
    const canEditGoal = !!(goal?.canEdit ?? legacy?.canEdit);
    const canViewProjectionReport = !!(report?.canView ?? legacy?.canView);
    const canEditProjectionReport = !!(report?.canEdit ?? legacy?.canEdit);

    return {
      canViewForecast,
      canEditForecast,
      canViewGoal,
      canEditGoal,
      canViewProjectionReport,
      canEditProjectionReport,
      canViewProjections: canViewForecast || canViewGoal || canViewProjectionReport,
      canAddProjections: !!(forecast?.canAdd || goal?.canAdd || report?.canAdd || legacy?.canAdd),
      canEditProjections: canEditForecast || canEditGoal || canEditProjectionReport,
      canDeleteProjections: !!(
        forecast?.canDelete ||
        goal?.canDelete ||
        report?.canDelete ||
        legacy?.canDelete
      ),
    };
  }

  canViewProjections() {
    return this.canAccessProjections().canViewProjections;
  }
  canviewCashflow() {
    this.getRolePermissionsDetailsFromLocalStorage();
    var canViewCashflow;
    var adminDetails = this.rolePermissions[0];
    var index = adminDetails?.permissions?.findIndex(
      (obj: any) => obj.module == 'Cash Flow'
    );
    if (index != undefined && index != -1) {
      canViewCashflow = adminDetails?.permissions[index]?.canView;
    }
    return canViewCashflow;
  }
}
